import { useMutation } from '@apollo/client'
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik'
import React, { useState } from 'react'
import * as Yup from 'yup'

import { Typography } from '@mui/material'
import Button from '@mui/material/Button'
import useToast from 'shared/components/Toast/useToast'
import { StyledLink } from 'shared/components/Typography'
import { ForgotPassword } from 'shared/graphql/auth.gql'
import { ForgotPasswordFormValues } from 'shared/models/user'
import {
    StyledButtonContainer,
    StyledSubtext,
    StyledTextInput,
} from 'shared/pages/SignUpSignIn/styled'
import Confirmation from '../Confirmation'
import SignUpSignIn from '../index'

const initialFormValues = {
    email: '',
}

const ForgotPasswordForm: React.FC = () => {
    const toast = useToast()
    const [emailSentTo, setEmailSentTo] = useState('')
    const [forgotPassword] = useMutation(ForgotPassword)

    const forgotPasswordValidationSchema = Yup.object().shape({
        email: Yup.string()
            .email('Invalid email')
            .required('Email is required'),
    })

    const handleResend = async () => {
        try {
            const res = await forgotPassword({
                variables: { email: emailSentTo },
            })
            if (
                res.data &&
                res.data.forgotPassword &&
                res.data.forgotPassword.success
            ) {
                toast({
                    title: 'Email sent',
                    message:
                        'Please click the link in your email to reset your password',
                    severity: 'success',
                })
            }
        } catch (err) {
            console.error('ERROR: ', err)
            return null
        }
    }

    const handleSubmit = async (
        values: ForgotPasswordFormValues,
        helpers: FormikHelpers<ForgotPasswordFormValues>
    ): Promise<null> => {
        const { email } = values
        try {
            const res = await forgotPassword({
                variables: { email: email },
            })
            if (
                res.data &&
                res.data.forgotPassword &&
                res.data.forgotPassword.success
            ) {
                setEmailSentTo(email)
            }
        } catch (err) {
            console.error('ERROR: ', err)
            toast({
                title: 'Something went wrong',
                message: 'Please check your email and try again',
            })
            return null
        } finally {
            helpers.setSubmitting(false)
        }
        return null
    }

    return (
        <React.Fragment>
            {emailSentTo ? (
                <Confirmation
                    subtitle="Reset your password"
                    blurb={`If an account exists for ${emailSentTo}, we will send instructions to reset your password.`}
                    onClickResend={handleResend}
                />
            ) : (
                <SignUpSignIn title="Forgot Password">
                    <Formik
                        initialValues={initialFormValues}
                        onSubmit={handleSubmit}
                        validationSchema={forgotPasswordValidationSchema}
                        validateOnBlur
                    >
                        {({ isSubmitting, errors, touched, values }) => (
                            <Form>
                                <Field name="email">
                                    {({ field }: FieldProps<string>) => (
                                        <StyledTextInput
                                            {...field}
                                            InputLabelProps={{ shrink: true }}
                                            id="email"
                                            size="small"
                                            error={
                                                !!errors.email && touched.email
                                            }
                                            helperText={
                                                !!errors.email &&
                                                touched.email &&
                                                errors.email
                                            }
                                            label="Email"
                                            placeholder="Email"
                                        />
                                    )}
                                </Field>
                                <StyledButtonContainer>
                                    <Button
                                        type="submit"
                                        disabled={
                                            isSubmitting ||
                                            !touched.email ||
                                            !values.email ||
                                            values.email.trim() === '' ||
                                            !!errors.email
                                        }
                                    >
                                        Send Link
                                    </Button>
                                </StyledButtonContainer>
                                <StyledSubtext>
                                    <Typography variant="body2">
                                        Having Trouble Logging In?
                                    </Typography>
                                    <StyledLink
                                        href={'mailto:registration@cpiny.com'}
                                        style={{ textDecoration: 'none' }}
                                    >
                                        Contact Support
                                    </StyledLink>
                                </StyledSubtext>
                            </Form>
                        )}
                    </Formik>
                </SignUpSignIn>
            )}
        </React.Fragment>
    )
}

export default ForgotPasswordForm
